import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
};

type FooterAction = {
  label: string;
  action: 'cookiePreference';
};

export type FooterNavItem = FooterLink | FooterAction;

export function useFooterLinks(): {
  primaryLinks: Array<FooterNavItem>;
  secondaryLinks: Array<FooterNavItem>;
  crossbrandLinks: Array<{ label: string; href: string }>;
} {
  const t = useTranslations('footer');

  return useMemo(
    () => ({
      primaryLinks: [
        { label: t('apartmentsNearMe'), href: '/apartments-near-me' },
        // Not /homes-for-rent-near-me, which 404s despite HP-8001 listing it.
        { label: t('housesNearMe'), href: '/houses-near-me' },
        { label: t('aboutUs'), href: '/about-us' },
        { label: t('termsOfUse'), href: 'https://www.zillow.com/corp/Terms.htm', external: true },
        {
          label: t('privacyNotice'),
          href: 'https://www.zillowgroup.com/zg-privacy-policy/',
          external: true,
        },
        {
          label: t('adChoices'),
          href: 'https://www.zillowgroup.com/zg-privacy-policy/#third-party',
          external: true,
        },
        { label: t('cookiePreference'), action: 'cookiePreference' },
        { label: t('help'), href: 'https://help.hotpads.com', external: true },
      ],
      secondaryLinks: [
        {
          label: t('feeds'),
          href: 'https://www.zillow.com/rentals-network/listing-feeds/',
          external: true,
        },
        { label: t('listYourRental'), href: '/list-your-rental' },
        { label: t('sitemap'), href: '/directory-sitemap' },
        { label: t('doNotSell'), href: 'https://privacy.zillowgroup.com/cookie', external: true },
      ],
      crossbrandLinks: [
        { label: t('zillowGroupBrands'), href: 'https://www.zillowgroup.com/' },
        { label: 'Zillow', href: 'https://www.zillow.com/' },
        { label: 'Trulia', href: 'https://www.trulia.com/' },
        { label: 'StreetEasy', href: 'https://streeteasy.com/' },
        { label: 'Out East', href: 'https://www.outeast.com/' },
      ],
    }),
    [t],
  );
}

/**
 * Static English fallback lists. Exported only for tests that snapshot the
 * default labels — production rendering goes through `useFooterLinks()`.
 */
export const primaryLinks: Array<FooterNavItem> = [
  { label: 'Apartments near me', href: '/apartments-near-me' },
  { label: 'Houses near me', href: '/houses-near-me' },
  { label: 'About us', href: '/about-us' },
  { label: 'Terms of use', href: 'https://www.zillow.com/corp/Terms.htm', external: true },
  {
    label: 'Privacy Notice',
    href: 'https://www.zillowgroup.com/zg-privacy-policy/',
    external: true,
  },
  {
    label: 'Ad Choices',
    href: 'https://www.zillowgroup.com/zg-privacy-policy/#third-party',
    external: true,
  },
  { label: 'Cookie Preference', action: 'cookiePreference' },
  { label: 'Help', href: 'https://help.hotpads.com', external: true },
];

export const secondaryLinks: Array<FooterLink> = [
  { label: 'Feeds', href: 'https://www.zillow.com/rentals-network/listing-feeds/', external: true },
  { label: 'List your rental', href: '/list-your-rental' },
  { label: 'Sitemap', href: '/directory-sitemap' },
  {
    label: 'Do Not Sell or Share My Personal Information',
    href: 'https://privacy.zillowgroup.com/cookie',
    external: true,
  },
];

export const crossbrandLinks: Array<{ label: string; href: string }> = [
  { label: 'Zillow Group® Brands:', href: 'https://www.zillowgroup.com/' },
  { label: 'Zillow', href: 'https://www.zillow.com/' },
  { label: 'Trulia', href: 'https://www.trulia.com/' },
  { label: 'StreetEasy', href: 'https://streeteasy.com/' },
  { label: 'Out East', href: 'https://www.outeast.com/' },
];

export const appBadges = {
  ios: {
    href: '/mobile-apps/ios?adgroup=homepageFooter',
  },
  android: {
    href: '/mobile-apps/android?adgroup=homepageFooter',
  },
} as const;

export const hamburgerAppBadges = {
  ios: {
    href: '/mobile-apps/ios?adgroup=hamburgerMenu',
  },
  android: {
    href: '/mobile-apps/android?adgroup=hamburgerMenu',
  },
} as const;

export const legalLinks = {
  hud: 'https://www.hud.gov/program_offices/fair_housing_equal_opp',
  fairHousingGuide: 'https://www.zillow.com/rental-manager/resources/fair-housing-guide/',
} as const;
